import { OFFER_CONDITION_LABELS, OfferAutoSendResult, OfferRecord, OfferSource, OfferType } from '../../api/OfferPortalApi';

/**
 * How a recorded offer is described wherever it appears — the Offer tab and the expandable
 * "Offer received" box on the case journey both read from here, so the two cannot describe the
 * same offer differently.
 */

export const OFFER_TYPE_LABELS: Record<OfferType, string> = {
  dsc: 'Driver Safety Course (DSC)',
  deferral: 'Deferral / deferred disposition',
  conviction: 'Conviction (neither DSC nor deferral)',
  appeal: 'Appeal / jury trial',
};

/** Lloyd, Aug 18 — the four ways an offer actually reaches the firm, plus a catch-all. */
export const OFFER_SOURCE_LABELS: Record<OfferSource, string> = {
  pretrial_notice: 'In the pre-trial notice',
  court_email: 'Email from the court or prosecutor',
  phone: 'Over the phone',
  hearing: 'At a hearing (in person or virtual)',
  other: 'Other',
};

export function formatOfferDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The terms in one line: "90 days, $150 fee, DSC also required", or a conviction's reduction.
 * Empty when there are none.
 */
export function offerTermsSummary(offer: OfferRecord): string {
  const parts: string[] = [];
  if (offer.reducedFrom && offer.reducedTo) {
    parts.push(`Charge lowered from ${offer.reducedFrom} to ${offer.reducedTo}`);
  }
  if (offer.deferralDays != null) parts.push(`${offer.deferralDays} days`);
  if (offer.deferralFeeUsd != null) parts.push(`$${offer.deferralFeeUsd} fee`);
  if (offer.dscAlsoRequired) parts.push('DSC also required');
  if (offer.dscDays != null) parts.push(`${offer.dscDays} days for the course`);
  for (const c of offer.conditions ?? []) parts.push(OFFER_CONDITION_LABELS[c] ?? c);
  return parts.join(', ');
}

/**
 * What happened to an offer's automatic send, in a sentence for staff, or null when the switch is
 * off and nothing was attempted (the offer then waits for Review and send, as it always did).
 */
export function describeAutoSend(result: OfferAutoSendResult | undefined): string | null {
  if (!result || result === 'switch-off') return null;
  if (result === 'failed') {
    return 'The offer is saved, but sending it to the client failed. Send it from the Offer tab.';
  }
  const went = [result.emailSent ? 'email' : null, result.smsSent ? 'text' : null].filter(Boolean);
  if (went.length === 0) {
    return `The offer is saved, but it could not be sent: ${result.problems.join('; ')}. Send it from the Offer tab.`;
  }
  const extra = result.problems.length ? ` (${result.problems.join('; ')})` : '';
  return `The offer was sent to the client by ${went.join(' and ')}${extra}.`;
}

/**
 * Whether the client has been told. Recorded-but-not-sent is a real state and the one staff most
 * need to see, so it is named rather than left blank.
 */
export function offerConveyanceLabel(offer: OfferRecord): string {
  if (!offer.conveyedAt) return 'Not yet sent to the client';
  const channels =
    offer.conveyedChannels === 'email+sms'
      ? 'by email and text'
      : offer.conveyedChannels === 'sms'
        ? 'by text'
        : 'by email';
  return `Sent to the client ${channels} on ${formatOfferDate(offer.conveyedAt)}`;
}
